import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serverRequest, serverFormRequest } from '../../util/server_request';
import { notifyUser } from '../../util/util';

export const scheduleKeys = {
  all: ['schedule'],
  uploads: (page, size, fundYear, search) => [...scheduleKeys.all, 'uploads', { page, size, fundYear, search }],
  monthDetail: (month) => [...scheduleKeys.all, 'month', month],
  memberDetail: (memberNo) => [...scheduleKeys.all, 'member', memberNo],
};

export const useScheduleUploads = (page = 0, size = 10, fundYear = '', search = '') => {
  return useQuery({
    queryKey: scheduleKeys.uploads(page, size, fundYear, search),
    queryFn: async () => {
      const params = new URLSearchParams({ page, size });
      if (fundYear) params.append('fund-year', fundYear);
      if (search) params.append('search', search);
      const result = await serverRequest('GET', `schedule?${params}`);
      return result.data || { content: [], totalElements: 0, totalPages: 0, number: 0 };
    },
    placeholderData: (prev) => prev,
    onError: (error) => {
      notifyUser('error', error, 'Data fetching failed');
    },
  });
};

export const useScheduleMonthDetail = (month, enabled = true) => {
  return useQuery({
    queryKey: scheduleKeys.monthDetail(month),
    queryFn: async () => {
      const result = await serverRequest('GET', `schedule/members?month=${month}`);
      return result.data || [];
    },
    enabled: enabled && !!month,
    onError: (error) => {
      notifyUser('error', error, 'Data Load Failed');
    },
  });
};

export const useScheduleMemberDetail = (memberNo, enabled = false) => {
  return useQuery({
    queryKey: scheduleKeys.memberDetail(memberNo),
    queryFn: async () => {
      const result = await serverRequest('GET', `members/${memberNo}`);
      return result.data;
    },
    enabled: enabled && !!memberNo,
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

export const useUploadSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ formData, pin }) => {
      return await serverFormRequest('POST', 'schedule/upload', formData, true, pin);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      notifyUser('success', 'Schedule uploaded successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

export const useValidateSchedule = () => {
  return useMutation({
    mutationFn: async (scheduleData) => {
      return await serverRequest('POST', 'schedule/validate', scheduleData);
    },
    onSuccess: (data) => {
      notifyUser('success', 'Schedule validated successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

export const useExportScheduleData = () => {
  return useMutation({
    mutationFn: async ({ month, exportType }) => {
      const result = await serverRequest('GET', `schedule/export?month=${month}&type=${exportType}`);
      return result.data;
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

export const useSyncSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await serverRequest('POST', 'schedule/sync');
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      notifyUser('success', data?.message || 'Schedule synced successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};
